/**
 * Tap action of a piece of Home content — the promotional modal and the carousel banners:
 * the contract types, the whitelist of app screens the console can point to, and the pure
 * checks the tabs run on their drafts.
 */

/** What a tap does in the app. Mirrors the kredit-api-MT contract. */
export type AppActionType = '' | 'internal' | 'external';

export interface AppAction {
    type: AppActionType;
    /** Route for `internal`, https URL for `external`, empty for no action. */
    target: string;
}

export interface AppInternalRoute {
    value: string;
    label: string;
}

/**
 * App screens content can open. The console offers only these, never a free-text route.
 *
 * Source of truth: the app's dispatcher (`credit_cx/lib/shared/navigation/redirect_dispatcher.dart`,
 * documented in `credit_cx/docs/api/SPEC_INTERNAL_REDIRECTS.md`), mirrored by the BFF
 * `INTERNAL_ROUTE_WHITELIST` and by kredit `INTERNAL_ROUTES`. Two deliberate omissions:
 * `/services/pay/select` (opens empty without `?productType=`, which this picker cannot add) and
 * the parametric detail routes (an id belongs to one customer; Home content is broadcast to all).
 */
export const APP_INTERNAL_ROUTES: readonly AppInternalRoute[] = [
    // Tabs
    { value: '/', label: 'Inicio' },
    { value: '/transactions', label: 'Mis compras' },
    { value: '/services', label: 'Servicios (seguros)' },
    { value: '/plans', label: 'Plan / recompensas' },
    { value: '/profile', label: 'Perfil' },
    // Focused pages
    { value: '/plans/rules', label: 'Reglas del programa de puntos' },
    { value: '/plans/activity', label: 'Actividad de puntos' },
    { value: '/profile/account', label: 'Datos de cuenta' },
    { value: '/profile/support', label: 'Soporte / ayuda' },
    { value: '/profile/settings', label: 'Ajustes de la app' },
    { value: '/profile/domiciliation', label: 'Domiciliación' },
    { value: '/payments', label: 'Historial de pagos' },
    { value: '/notifications', label: 'Notificaciones' },
    { value: '/pay/select', label: 'Pagar o adelantar cuotas' },
    { value: '/pagare-pending', label: 'Pagaré pendiente de firma' },
];

/** Narrows whatever the backend sent to a known action type; anything else means no action. */
export function toAppActionType(value: unknown): AppActionType {
    return value === 'internal' || value === 'external' ? value : '';
}

export function findAppInternalRoute(value: string): AppInternalRoute | undefined {
    return APP_INTERNAL_ROUTES.find(route => route.value === value);
}

/**
 * Inline error for a draft action; empty when it can be published. Spanish, ready to show.
 * `subject` names what the customer taps («la imagen», «el banner»).
 */
export function validateAppAction(action: AppAction, subject: string): string {
    if (action.type === 'internal') {
        return findAppInternalRoute(action.target) ? '' : `Elige la pantalla que se abrirá al tocar ${subject}`;
    }
    if (action.type === 'external') {
        if (!action.target) return `Escribe el enlace que se abrirá al tocar ${subject}`;
        if (!action.target.startsWith('https://')) return 'El enlace debe empezar por https://';
        try {
            new URL(action.target);
        } catch {
            return 'El enlace no es una dirección válida';
        }
    }
    return '';
}

/** Hint shown next to a preview describing what a tap does. */
export function describeAppTap(action: AppAction): string {
    if (action.type === 'internal') {
        const route = findAppInternalRoute(action.target);
        return route ? `Al tocar abre: ${route.label}` : 'Al tocar abre: una pantalla de la app';
    }
    if (action.type === 'external') {
        return 'Al tocar abre: enlace externo';
    }
    return 'Solo se cierra, sin acción';
}
